import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { TIERS } from "../../lib/personnel";
import api from "../../lib/api";
import { toast } from "sonner";

const HEADER_ROW = [
  "Full Name", "Tier", "Monthly Rate", "Hourly Rate", "Status",
];

// Parses CSV / TSV pasted text into the rows shape the backend expects.
// Tolerant: header row optional but recommended; commas inside quoted strings handled.
function parseTable(text) {
  const lines = text.replace(/\r\n/g, "\n").split("\n").map((l) => l.trimEnd()).filter(Boolean);
  if (lines.length === 0) return { rows: [], unparsed: [] };

  const sep = (lines[0].match(/\t/g) || []).length > (lines[0].match(/,/g) || []).length ? "\t" : ",";
  const splitLine = (line) => {
    if (sep === "\t") return line.split("\t").map((s) => s.trim());
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') { inQ = !inQ; continue; }
      if (c === "," && !inQ) { out.push(cur.trim()); cur = ""; continue; }
      cur += c;
    }
    out.push(cur.trim());
    return out;
  };

  // Try to detect header.
  const first = splitLine(lines[0]).map((c) => c.toLowerCase());
  const hasHeader = first.some((c) => c.includes("full name") || c.includes("fullname") || c.includes("tier"));
  const headers = hasHeader ? first : HEADER_ROW.map((h) => h.toLowerCase());
  const dataLines = hasHeader ? lines.slice(1) : lines;

  const idx = (label) => {
    const aliases = label.split("|");
    for (const a of aliases) {
      const i = headers.findIndex((h) => h.replace(/[^a-z]/g, "") === a.replace(/[^a-z]/g, ""));
      if (i >= 0) return i;
    }
    return -1;
  };
  const cols = {
    fullName: idx("fullname|full name"),
    tier:     idx("tier"),
    monthly:  idx("monthlyrate|monthly rate|monthly"),
    hourly:   idx("hourlyrate|hourly rate|hourly"),
    status:   idx("status"),
  };

  const num = (s) => {
    if (!s) return 0;
    const cleaned = String(s).replace(/[^0-9.\-]/g, "");
    return Number(cleaned) || 0;
  };

  const rows = [];
  const unparsed = [];
  for (const line of dataLines) {
    const c = splitLine(line);
    const name = (cols.fullName >= 0 ? c[cols.fullName] : c[0]) || "";
    if (!name) continue;
    const tier = cols.tier >= 0 ? c[cols.tier] : "";
    const matchedTier = TIERS.find((t) => t.toLowerCase() === (tier || "").toLowerCase());
    if (!matchedTier) unparsed.push({ line, reason: `unknown tier '${tier}'` });
    rows.push({
      fullName: name.trim(),
      tier: matchedTier || TIERS[TIERS.length - 1],
      monthlyRate:  num(c[cols.monthly] ?? 0),
      hourlyRate:   num(c[cols.hourly] ?? 0),
      status: ((cols.status >= 0 ? c[cols.status] : "active") || "active").toLowerCase(),
    });
  }
  return { rows, unparsed };
}

export default function BulkUploadDialog({ open, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const parsed = parseTable(text);

  const upload = async () => {
    if (parsed.rows.length === 0) {
      toast.error("No rows to upload");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/personnel/bulk", { rows: parsed.rows });
      setResult(data);
      toast.success(`Uploaded · ${data.created.length} created · ${data.updated.length} updated`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ""));
    reader.readAsText(f);
  };

  const close = () => { setText(""); setResult(null); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-2xl" data-testid="bulk-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracked text-base">Bulk upload personnel</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-[var(--soft-fg)] mt-2">
          Paste from a spreadsheet (CSV or tab-separated) or upload a file. First row should be a header.
          Columns: <strong>{HEADER_ROW.join(" · ")}</strong>. Rows matching an existing person by full name update; new names are created.
        </div>

        <div className="mt-4 flex items-center gap-4">
          <label className="text-xs uppercase font-display tracked-tight text-[var(--soft-fg)] cursor-pointer hover:text-[var(--ink)]">
            Upload file
            <input type="file" accept=".csv,.tsv,.txt" onChange={onFile} className="hidden" data-testid="bulk-file-input" />
          </label>
          <span className="eyebrow text-[var(--soft-fg)]">or paste below</span>
        </div>

        <textarea
          className="w-full h-48 mt-2 px-3 py-2 border border-[var(--rule)] text-xs font-mono-num bg-white focus:border-[var(--ink)] outline-none"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={"Full Name, Tier, Monthly Rate, Hourly Rate, Status\nMark Craven, Principal, 36000, 215, active\n…"}
          data-testid="bulk-paste-area"
        />

        {parsed.rows.length > 0 && (
          <div className="mt-3 text-xs" data-testid="bulk-preview">
            <strong className="text-[var(--ink)]">{parsed.rows.length} row{parsed.rows.length !== 1 ? "s" : ""} parsed</strong>
            {parsed.unparsed.length > 0 && (
              <span className="text-[#6B2F2A] ml-3">{parsed.unparsed.length} warning{parsed.unparsed.length !== 1 ? "s" : ""}</span>
            )}
            <div className="mt-1 text-[var(--soft-fg)] max-h-24 overflow-y-auto">
              {parsed.rows.slice(0, 5).map((r) => (
                <div key={`${r.fullName}-${r.tier}`}>· {r.fullName} — {r.tier} — ${r.monthlyRate.toLocaleString()}/mo</div>
              ))}
              {parsed.rows.length > 5 && <div>…and {parsed.rows.length - 5} more</div>}
            </div>
          </div>
        )}

        {result && (
          <div className="mt-4 p-3 bg-[var(--paper-soft)] text-xs" data-testid="bulk-result">
            <strong>Done.</strong> Created {result.created.length}, updated {result.updated.length}.
            {result.errors?.length > 0 && (
              <div className="mt-1 text-[#6B2F2A]">{result.errors.length} error{result.errors.length !== 1 ? "s" : ""}.</div>
            )}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-3">
          <button onClick={close} className="px-4 py-2 text-xs uppercase font-display tracked-tight text-[var(--soft-fg)] hover:text-[var(--ink)]">Close</button>
          <button onClick={upload} disabled={busy || parsed.rows.length === 0} className="px-4 py-2 bg-[var(--ink)] text-white text-xs uppercase font-display tracked-tight disabled:opacity-50" data-testid="bulk-upload-save">
            {busy ? "Uploading…" : `Upload ${parsed.rows.length || ""} rows`}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
